import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../../common/types';
import { ServiceExtras } from '../extras/ServiceExtras';
import { ExtraFileThumb } from '../extras/ExtraFileThumb';
import { ServiceExtraAction, ServiceExtraItem } from '../extras/serviceExtraTypes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the extras box; each part uses its own slice of it */
import styles from '../extras/ServiceExtras.module.scss';
/* eslint-enable css-modules/no-unused-class */
import type { YouTubeExtraItemView } from './youtubeExtraRules';
import {
  YOUTUBE_ACTION_LABELS,
  youtubeChipText,
  youtubeChipTone,
  youtubeItemTitle,
  youtubeSentence,
  youtubeSentenceIsBad,
} from './youtubeExtraTexts';
import { youtubeExtrasMessages as m } from './youtubeExtrasMessages';
import { useYouTubeExtras } from './useYouTubeExtras';

interface YouTubeExtrasProps {
  accountId: number;
  item: CatalogItem;
  /** The demo artist: the box with no button. */
  readOnly: boolean;
}

/** The address that Open opens: the watch page of the record. */
function openUrlOf(extra: YouTubeExtraItemView): string | null {
  return extra.record?.externalUrl ?? null;
}

/**
 * "YouTube extras" in the YouTube row of "Where it plays" (design service-extras a1, a2): one item
 * for each full-song video of the song (music video, lyric video, visualizer) with its state on
 * YouTube, and the one-off publish of the PublishDialog. Manual only (owner decision 2026-10-18:
 * no YouTube auto upload), so the box has no switch.
 */
const YouTubeExtrasComponent: React.FC<YouTubeExtrasProps> = ({ accountId, item, readOnly }) => {
  const intl = useIntl();
  const { items: views, run } = useYouTubeExtras({ accountId, item, readOnly });

  const items = useMemo(
    (): ServiceExtraItem[] =>
      views.map((extra) => {
        const actions = extra.actions.flatMap(({ action, look }): ServiceExtraAction[] => {
          const label = intl.formatMessage(YOUTUBE_ACTION_LABELS[action]);
          if (action === 'open') {
            const href = openUrlOf(extra);
            return href === null ? [] : [{ id: action, label, look: 'link', href }];
          }
          return [{ id: action, label, look, onClick: () => run(action, extra) }];
        });
        const details = (
          <>
            {extra.newerRevision !== null && (
              <span className={styles.newer}>{intl.formatMessage(m.newer, { revision: extra.newerRevision })}</span>
            )}
            {extra.progress !== null && (
              <span className={styles.bar} role="progressbar" aria-valuenow={Math.round(extra.progress)}>
                <i style={{ width: `${Math.min(100, Math.max(0, extra.progress))}%` }} />
              </span>
            )}
          </>
        );
        const poster = extra.readyAsset?.posterUrl ?? extra.readyAsset?.thumbUrl ?? null;
        return {
          id: `youtube-${extra.id}`,
          title: youtubeItemTitle(intl, extra),
          thumb: (
            <ExtraFileThumb
              shape="wide"
              image={poster}
              empty={extra.state === 'noVideo'}
              dim={extra.state === 'notConnected'}
              mark={extra.state === 'notConnected' || extra.state === 'noVideo' ? null : 'youtube'}
            />
          ),
          chips: extra.chips.map((chip) => ({
            id: chip.kind,
            text: youtubeChipText(intl, chip),
            tone: youtubeChipTone(chip),
          })),
          status: youtubeSentence(intl, extra),
          statusIsBad: youtubeSentenceIsBad(extra),
          details,
          actions,
          waits: extra.waits,
        };
      }),
    [intl, run, views]
  );

  return <ServiceExtras label={intl.formatMessage(m.label)} items={items} testId="youtube-extras" />;
};

export const YouTubeExtras = TypedMemo(YouTubeExtrasComponent);
